//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// event-scheduler-time-input imports
import { eventSchedulerTimeInputFragments } from "./EventSchedulerTimeInput.gql";
import * as s from "./EventSchedulerTimeInput.styles";
import { IContainer, IProps, IState } from "./EventSchedulerTimeInputTypes";
// other imports
import { RoomOptionEnum } from "#webapp/src/components/event-scheduler";
import { EventSchedulerSuggestedTimes } from "#webapp/src/components/event-scheduler-suggested-times";
import { RangeAndDurationPicker } from "#webapp/src/components/range-and-duration-picker";
import { SuggestedTimeTileLarge } from "@clockwise/web-commons/src/components/suggested-times-row";
// state
import { rescheduleEventParent } from "#webapp/src/mutations";
import { setActiveViewEvent } from "#webapp/src/state/actions/chrome-extension.actions";
import { makeEventSchedulerViewActive } from "#webapp/src/state/actions/feed-scheduler.actions";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
// util
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { getEventTimeForSchedulingSlot } from "#webapp/src/util/event-time.util";
import { niceRooms } from "#webapp/src/util/event.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { EScheduleRange, slotsEqual } from "#webapp/src/util/scheduler.util";
import { getMomentForSlot } from "#webapp/src/util/scheduling-slot.util";
import { getCurrentWorkingGroup } from "#webapp/src/util/working-group.util";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
// material-ui
import { Button, Select, SelectOption } from "@clockwise/design-system";
import { EcosystemEnum, SchedulingAttendanceSummaryTag } from "@clockwise/schema";
import { isMakeItWorkSlot } from "@clockwise/web-commons/src/components/make-it-work/MakeItWork";
import { useExperiment } from "@clockwise/web-commons/src/util/ExperimentsContext";
import { Divider, Tooltip } from "@material-ui/core";
import { Business, ChevronLeft, Close, Edit } from "@material-ui/icons";
import toast from "react-hot-toast";
import { setHighlightedSlot } from "../../state/actions/planner-highlight.actions";

const RoomPicker = (props: {
  includeRoom: RoomOptionEnum;
  onIncludeRoomChange: (includeRoom: RoomOptionEnum) => void;
  roomsOverride: string[];
  reschedulerEvent: ISchema.IEvent | undefined;
  specificRoomLabel: string;
}) => {
  const ecosystem = useEcosystem();
  const isM365UnhideUIExperimentOn = useExperiment("M365UnhideUI");
  const hideUI = ecosystem === EcosystemEnum.Microsoft && !isM365UnhideUIExperimentOn;

  const {
    includeRoom,
    onIncludeRoomChange,
    roomsOverride,
    reschedulerEvent,
    specificRoomLabel,
  } = props;
  return (
    <div className="cw-flex cw-items-center cw-gap-2 cw-w-full cw-mb-2">
      <Business className="cw-h-4 cw-w-4 cw-text-subtle" />
      <div className="cw-w-[224px]">
        <Select
          value={includeRoom}
          onChange={onIncludeRoomChange}
          name="Include Room"
          cw-id="meeting-room-picker"
          fullWidth
        >
          <SelectOption value={RoomOptionEnum.WithRoom}>With a meeting room</SelectOption>
          <SelectOption value={RoomOptionEnum.WithoutRoom}>Without a meeting room</SelectOption>
          {!hideUI && (
            <>
              <Divider />
              <SelectOption value={RoomOptionEnum.SpecificRoom}>
                {roomsOverride.length > 0 || (reschedulerEvent && specificRoomLabel)
                  ? specificRoomLabel
                  : "Specific room"}
              </SelectOption>
            </>
          )}
        </Select>
      </div>
    </div>
  );
};

//////////////////
// COMPONENT
//////////////////
class EventSchedulerTimeInputCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      specifiedDate: props.specifiedDate,
      range: props.range,
      editMode: !!this.props.open,
      minutesDuration: props.minutesDuration || 30,
      includeRoom: props.includeRoom,
      showDatePicker: false,
      savingEvent: false,
      conflictResolutionEnabled: false,
      openConflictResolution: false,
      conflictResolutionDefragId: undefined,
    };
  }

  public componentDidMount() {
    const { schedulingResponseErrorable, useAutoSchedule } = this.props;
    if (
      !schedulingResponseErrorable ||
      schedulingResponseErrorable.__typename === "GraphEntityError" ||
      schedulingResponseErrorable.slots.length < 1
    ) {
      return;
    }

    // if no conenvient times, dont auto select anything
    if (
      schedulingResponseErrorable.qualifiers.indexOf(
        "NoConvenientTimesWithRooms" as ISchema.SchedulingResponseQualifier,
      ) !== -1
    ) {
      return;
    }

    if (useAutoSchedule) {
      // Auto select the second slot "Best", else the first "Soonest"
      let autoScheduleIndex = schedulingResponseErrorable?.slots?.length >= 2 ? 1 : 0;
      const bestSlotIndex = schedulingResponseErrorable?.slots?.findIndex(
        (slot: ISchema.ISchedulingSlot) =>
          slot.summary.tags.includes(SchedulingAttendanceSummaryTag.Best),
      );
      if (bestSlotIndex >= 0) {
        autoScheduleIndex = bestSlotIndex;
      }
      this.maybeTimeCallback(schedulingResponseErrorable.slots[autoScheduleIndex]);
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { schedulingResponseErrorable, selectedSlot } = nextProps;

    if (
      !schedulingResponseErrorable ||
      schedulingResponseErrorable.__typename === "GraphEntityError"
    ) {
      throw new Error("Scheduling response errorable is not defined");
    }

    let nextSlot: ISchema.ISchedulingSlot | undefined = undefined;

    // Try to find a slot in the new suggestions that has same timing and room.
    const slotWithSameTimeAndRoom = schedulingResponseErrorable?.slots?.find((slot) =>
      slotsEqual(selectedSlot, slot),
    );

    if (slotWithSameTimeAndRoom) {
      nextSlot = slotWithSameTimeAndRoom;
    }

    const newState = {
      ...(nextProps.minutesDuration && this.props.minutesDuration !== nextProps.minutesDuration
        ? { minutesDuration: nextProps.minutesDuration }
        : {}),
      ...(nextProps.range && this.props.range !== nextProps.range
        ? { range: nextProps.range }
        : {}),
      ...(nextProps.specifiedDate && this.props.specifiedDate !== nextProps.specifiedDate
        ? { specifiedDate: nextProps.specifiedDate }
        : {}),
      ...(nextProps.includeRoom && this.props.includeRoom !== nextProps.includeRoom
        ? { includeRoom: nextProps.includeRoom }
        : {}),
    };

    this.setState((prevState) => ({ ...prevState, ...newState }));
    if (!slotsEqual(nextSlot, this.props.selectedSlot)) {
      this.maybeTimeCallback(nextSlot);
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private expand = (expand: boolean) => {
    this.setState({ editMode: expand });
  };

  private close = () => {
    const { reschedulerEvent } = this.props;
    if (!reschedulerEvent) {
      this.expand(false);
    } else {
      track(TrackingEvents.SCHEDULING.RESCHEDULER_CANCELLED, {
        schedulingSessionId: this.props.schedulingSessionId,
        externalEventId: reschedulerEvent.eventKey.externalEventId,
      });
      this.goToEventViewFromRescheduler(false);
    }
  };

  private goToEventViewFromRescheduler = (recentlyRescheduled: boolean) => {
    const { reschedulerEvent, activeViewEventCalendarId } = this.props;
    if (!reschedulerEvent) {
      return;
    }

    const externalEventId = reschedulerEvent.eventKey.externalEventId;
    this.props.makeEventSchedulerViewActive(false);

    this.props.setActiveViewEvent(externalEventId, activeViewEventCalendarId, recentlyRescheduled);
  };

  private maybeTimeCallback = (
    newSlot?: ISchema.ISchedulingSlot,
    specificRoomClicked?: boolean,
  ) => {
    const {
      minutesDuration,
      includeRoom,
      range,
      specifiedDate,
      conflictResolutionDefragId,
    } = this.state;
    const { schedulingResponseErrorable } = this.props;

    const uncrawledCalendarsCount =
      (schedulingResponseErrorable &&
        schedulingResponseErrorable.__typename === "SchedulingResponse" &&
        schedulingResponseErrorable.uncrawledCalendars.length) ||
      0;

    if (this.props.onChange) {
      this.props.onChange(
        minutesDuration,
        includeRoom,
        range,
        specifiedDate,
        uncrawledCalendarsCount,
        newSlot,
        specificRoomClicked,
        conflictResolutionDefragId,
      );
    }
  };

  private onIncludeRoomChange = (includeRoom: RoomOptionEnum) => {
    track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_TIME_INPUT_INCLUDE_ROOM_CHANGE, {
      includeRoom,
      schedulingSessionId: this.props.schedulingSessionId,
    }); // analytics
    this.setState({ includeRoom }, () => {
      this.maybeTimeCallback(this.props.selectedSlot, includeRoom === RoomOptionEnum.SpecificRoom);
    });
  };

  private onRangeChange = (range: EScheduleRange) => {
    track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_TIME_INPUT_RANGE_CHANGE, {
      range,
      schedulingSessionId: this.props.schedulingSessionId,
    }); // analytics
    if (range === EScheduleRange.SpecificDate || range === EScheduleRange.AroundDate) {
      this.setState({ range, showDatePicker: true });
    } else {
      this.setState({ range, specifiedDate: null }, () => {
        this.maybeTimeCallback(this.props.selectedSlot);
      });
    }
  };

  private onMaybeTimeSelection = (
    selectedSlot: ISchema.ISchedulingSlot,
    trackingIndex: number,
    slots: ISchema.ISchedulingSlot[],
  ) => {
    this.onTimeSelection(selectedSlot, trackingIndex, slots);
  };

  private onTimeSelection = (
    newSlot?: ISchema.ISchedulingSlot,
    trackingIndex?: number,
    slots?: ISchema.ISchedulingSlot[],
  ) => {
    track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_TIME_INPUT_SLOT_SELECTED, {
      schedulingSessionId: this.props.schedulingSessionId,
      index: trackingIndex,
    }); // analytics

    const hasAMakeItWorkOptions = slots?.some((slot) => isMakeItWorkSlot(slot));
    const selectedSlotIsMakeItWork = isMakeItWorkSlot(newSlot);

    if (hasAMakeItWorkOptions) {
      if (selectedSlotIsMakeItWork) {
        track(TrackingEvents.EVENT_SCHEDULER.SCHEDULE_BY_MOVING.USER_SELECTED_MAKE_IT_WORK_SLOT);
      } else {
        track(
          TrackingEvents.EVENT_SCHEDULER.SCHEDULE_BY_MOVING.USER_SELECTED_A_NON_MAKE_IT_WORK_SLOT,
        );
      }
    }

    this.maybeTimeCallback(newSlot);
  };

  private rescheduleEvent = () => {
    const { reschedulerEvent, org, relay, selectedSlot } = this.props;
    if (!reschedulerEvent || !selectedSlot) {
      return;
    }

    track(TrackingEvents.SCHEDULING.RESCHEDULER_RESCHEDULED, {
      schedulingSessionId: this.props.schedulingSessionId,
      externalEventId: reschedulerEvent.eventKey.externalEventId,
    });

    this.setState({ savingEvent: true });
    const eventTime = getEventTimeForSchedulingSlot(selectedSlot);
    const input = {
      eventTime,
      orgRelayId: org.id,
      externalEventId: reschedulerEvent.eventKey.externalEventId,
    };

    // fire off the mutation
    rescheduleEventParent(
      relay.environment,
      input,
      this.onRescheduleSuccess,
      this.onRescheduleError,
    );
  };

  // success callback after mutation
  private onRescheduleSuccess = () => {
    this.props.setHighlightedSlot(null);
    this.setState({ savingEvent: false });
    this.goToEventViewFromRescheduler(true);
    toast.success("Your event was moved.");
  };

  // error callback after mutation
  private onRescheduleError = (_error: any) => {
    this.props.setHighlightedSlot(null);
    this.setState({ savingEvent: false });
    toast.error("Sorry, an error occurred moving your meeting.");
  };

  private onHoverSlot = (slot: ISchema.ISchedulingSlot, hovered: boolean, duration: number) => {
    const startMillis = getMomentForSlot(slot).valueOf();
    PostMessageManager.sendShowEventOnCalendar(startMillis, duration, hovered);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderViewMode = (schedulingResponse: ISchema.ISchedulingResponse) => {
    const { persons, selectedSlot } = this.props;

    // Get slot index
    const slotIndex = schedulingResponse.slots.findIndex(
      (slot) => JSON.stringify(slot.timeRange) === JSON.stringify(selectedSlot?.timeRange),
    );

    const roomId = selectedSlot && selectedSlot.roomImpacts?.[0]?.roomId;
    let roomName = "";

    if (roomId) {
      const workingGroup = getCurrentWorkingGroup(this.props.org);
      if (workingGroup) {
        roomName = workingGroup.rooms.find((room) => room.roomId === roomId)?.name || "";
      }
    }

    return (
      <div
        className="cw-py-2 cw-mr-2"
        onMouseEnter={() =>
          selectedSlot && this.onHoverSlot(selectedSlot, true, this.state.minutesDuration)
        }
        onMouseLeave={() =>
          selectedSlot && this.onHoverSlot(selectedSlot, false, this.state.minutesDuration)
        }
      >
        <div style={s.viewModeRow} onClick={() => this.expand(true)}>
          {selectedSlot ? (
            <Tooltip
              placement="top"
              aria-label="change time"
              title={selectedSlot ? "Click to select a new time" : ""}
            >
              <div className="cw-mt-3" cw-id="scheduler-change-time-button">
                <SuggestedTimeTileLarge
                  key={slotIndex}
                  slot={selectedSlot}
                  index={slotIndex}
                  isLoading={false}
                  isSelected={false}
                  roomName={roomName}
                  onSelect={() => this.onTimeSelection(selectedSlot, slotIndex)}
                  people={persons}
                  onMouseEnter={(hoveredSlot: ISchema.ISchedulingSlot) => {
                    const durationMinutes =
                      (hoveredSlot.timeRange.endTime.millis -
                        hoveredSlot.timeRange.startTime.millis) /
                      (1000 * 60);
                    PostMessageManager.sendShowEventOnCalendar(
                      hoveredSlot.timeRange.startTime.millis,
                      durationMinutes,
                      true,
                    );
                  }}
                  onMouseLeave={() => {
                    PostMessageManager.sendShowEventOnCalendar(null, null, false);
                  }}
                />
              </div>
            </Tooltip>
          ) : (
            <div className="cw-flex cw-flex-row cw-items-center">
              <div className="cw-text-14 cw-text-neutral cw-flex-grow cw-ml-1">Select a time</div>
              <Button
                cw-id="scheduler-select-time-button"
                size="small"
                variant="text"
                startIcon={Edit}
                onClick={() => this.expand(true)}
              />
            </div>
          )}
        </div>
        {this.props.timeError ? <div style={s.error}>{this.props.timeError}</div> : ""}
      </div>
    );
  };

  private onRangeDurationChange = (
    minutesDuration: number,
    range: EScheduleRange,
    specifiedDate: Date | null,
  ) => {
    this.setState({ minutesDuration, range, specifiedDate }, () => {
      this.maybeTimeCallback(this.props.selectedSlot);
    });
  };

  private renderEditMode = (schedulingResponse: ISchema.ISchedulingResponse) => {
    const { minutesDuration, includeRoom } = this.state;
    const { reschedulerEvent, roomsOverride, isLoading, showCloseButton } = this.props;
    const workingGroup = getCurrentWorkingGroup(this.props.org);

    const firstRoom =
      workingGroup && workingGroup.rooms?.find((room) => room.roomId === roomsOverride[0]);
    let specificRoomLabel =
      roomsOverride.length > 0
        ? roomsOverride.length > 1
          ? `${roomsOverride.length} rooms selected`
          : (firstRoom && firstRoom.name) || roomsOverride[0]
        : "No rooms selected";
    if (reschedulerEvent && includeRoom === RoomOptionEnum.SpecificRoom) {
      const reschedulerRoom = niceRooms(reschedulerEvent.attendees, 15);
      if (reschedulerRoom) {
        specificRoomLabel = reschedulerRoom;
      }
    }

    return (
      <div style={{ ...s.editModeContainer }}>
        <div className="cw-flex cw-items-center cw-gap-1 cw-ml-0.5 cw-mb-2">
          {showCloseButton && (
            <Button
              variant="text"
              startIcon={reschedulerEvent ? Close : ChevronLeft}
              onClick={() => this.close()}
              cw-id="choose-time-back-button"
            />
          )}
          <div className="cw-heading-xl">Choose a time</div>
        </div>
        <div className="cw-relative cw-flex cw-flex-col cw-mx-[10px] cw-gap-1 cw-mb-2">
          <RangeAndDurationPicker
            onChange={this.onRangeDurationChange}
            minutesDuration={this.state.minutesDuration}
            range={this.state.range}
            specifiedDate={this.state.specifiedDate}
            isReschedulerEvent={!!reschedulerEvent}
          />
          <RoomPicker
            includeRoom={includeRoom}
            onIncludeRoomChange={this.onIncludeRoomChange}
            roomsOverride={roomsOverride}
            reschedulerEvent={reschedulerEvent}
            specificRoomLabel={specificRoomLabel}
          />
        </div>
        <div className="cw-overflow-auto cw-flex-grow">
          <EventSchedulerSuggestedTimes
            schedulingResponse={schedulingResponse}
            org={this.props.org}
            minutesDuration={minutesDuration}
            onSelect={this.onMaybeTimeSelection}
            selectedSlot={this.props.selectedSlot}
            includeRoom={includeRoom !== RoomOptionEnum.WithoutRoom}
            persons={this.props.persons}
            resetTimeframe={() => this.onRangeChange(EScheduleRange.NextSeven)}
            environment={this.props.relay.environment}
            isLoading={isLoading}
            onSetDate={(date: Date) =>
              this.onRangeDurationChange(
                this.state.minutesDuration,
                EScheduleRange.SpecificDate,
                date,
              )
            }
            setHighlightedSlot={this.props.setHighlightedSlot}
          />
        </div>
        {reschedulerEvent ? (
          <div className="cw-flex cw-flex-col cw-gap-2" style={s.buttonContainer}>
            <Button
              sentiment="positive"
              onClick={() => this.rescheduleEvent()}
              disabled={this.state.savingEvent || !this.props.selectedSlot}
              fullWidth
              cw-id="reschedule-submit-button"
            >
              {this.state.savingEvent ? "Recheduling" : "Reschedule"}
            </Button>
            <Button variant="text" onClick={() => this.close()} fullWidth cw-id="cancel-button">
              Cancel
            </Button>
          </div>
        ) : (
          showCloseButton && (
            <div style={s.buttonContainer}>
              <Button
                sentiment="positive"
                onClick={() => this.close()}
                disabled={!this.props.selectedSlot}
                fullWidth
                cw-id="event-scheduler-continue-button"
              >
                Continue
              </Button>
            </div>
          )
        )}
      </div>
    );
  };

  public render() {
    const { schedulingResponseErrorable } = this.props;
    const { editMode } = this.state;

    if (
      !schedulingResponseErrorable ||
      schedulingResponseErrorable.__typename === "GraphEntityError"
    ) {
      return <div>Sorry, something went wrong.</div>;
    }

    return editMode
      ? this.renderEditMode(schedulingResponseErrorable)
      : this.renderViewMode(schedulingResponseErrorable);
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    schedulingSessionId: state.feedScheduler.schedulingSessionId,
    reschedulerEvent: state.feedScheduler.reschedulerEvent,
    activeViewEventCalendarId: state.webExtension.activeViewEvent.calendarId,
  };
}

const EventSchedulerTimeInputRedux = connect(mapStateToProps, {
  makeEventSchedulerViewActive,
  setActiveViewEvent,
  setHighlightedSlot,
})(EventSchedulerTimeInputCmpt);

//////////////////
// RELAY
//////////////////
export const EventSchedulerTimeInput = createFragmentContainer<IContainer>(
  EventSchedulerTimeInputRedux,
  eventSchedulerTimeInputFragments,
);
